/**
 * Magic Import — paste a URL or drop a file to auto-extract event details.
 *
 * States: idle → loading → done (renders ExtractionPreview) | error
 * Supports: URLs (event websites), images (invitations), PDFs (flyers).
 */
import { useState, useRef, useCallback } from 'react';
import {
  Alert, Box, Button, CircularProgress, LinearProgress,
  Paper, TextField, Typography,
} from '@mui/material';
import { AutoAwesome, CloudUpload, Language } from '@mui/icons-material';
import { conciergeApi } from '../services/concierge-api-client';
import type { EventSiteData } from '../types/event-data';
import ExtractionPreview from './ExtractionPreview';

const LOADING_STAGES = [
  'Reading your site...',
  'Capturing screenshot...',
  'Discovering subpages...',
  'Extracting details with AI...',
  'Almost done...',
];

const ACCEPTED_FILE_TYPES = '.jpg,.jpeg,.png,.pdf,.webp';
const ACCEPTED_MIME_PREFIXES = ['image/', 'application/pdf'];

interface Props {
  eventType: string;
  onExtracted: (data: EventSiteData, extractionId: string) => void;
  onSkip: () => void;
}

export default function MagicImportInput({ eventType, onExtracted, onSkip }: Props) {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingStage, setLoadingStage] = useState(0);
  const [error, setError] = useState('');
  const [extractedData, setExtractedData] = useState<EventSiteData | null>(null);
  const [extractionId, setExtractionId] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const advanceStages = useCallback(() => {
    let stage = 0;
    const interval = setInterval(() => {
      stage++;
      if (stage < LOADING_STAGES.length) {
        setLoadingStage(stage);
      } else {
        clearInterval(interval);
      }
    }, 2500);
    return interval;
  }, []);

  const handleUrlSubmit = async () => {
    let trimmed = url.trim();
    if (!trimmed) return;

    // Auto-prefix https:// if no protocol provided
    if (!trimmed.startsWith('http://') && !trimmed.startsWith('https://')) {
      trimmed = `https://${trimmed}`;
      setUrl(trimmed);
    }

    setError('');
    setLoading(true);
    setLoadingStage(0);
    const interval = advanceStages();

    try {
      const result = await conciergeApi.scrapePreview(trimmed, eventType);
      setExtractedData(result.event_data);
      setExtractionId(result.extraction_id);
    } catch (e: any) {
      const msg = e.response?.data?.detail || e.message || 'Extraction failed';
      setError(msg);
    } finally {
      clearInterval(interval);
      setLoading(false);
    }
  };

  const handleFileDrop = async (file: File) => {
    if (!ACCEPTED_MIME_PREFIXES.some(p => file.type.startsWith(p))) {
      setError('Please upload an image (JPG, PNG) or PDF file.');
      return;
    }

    setError('');
    setLoading(true);
    setLoadingStage(0);
    // File extraction is faster — single Gemini call
    setLoadingStage(3); // Jump to "Extracting details..."

    try {
      const base64 = await fileToBase64(file);
      const result = await conciergeApi.extractFile(base64, file.type, eventType);
      setExtractedData(result.event_data);
      setExtractionId(result.extraction_id);
    } catch (e: any) {
      const msg = e.response?.data?.detail || e.message || 'Extraction failed';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileDrop(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileDrop(file);
  };

  // If extraction is complete, show the preview
  if (extractedData) {
    return (
      <ExtractionPreview
        data={extractedData}
        extractionId={extractionId}
        onConfirm={(editedData) => onExtracted(editedData, extractionId)}
        onRetry={() => {
          setExtractedData(null);
          setExtractionId('');
          setUrl('');
        }}
      />
    );
  }

  // Loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, py: 6 }}>
        <CircularProgress size={48} sx={{ color: 'primary.main' }} />
        <Typography variant="h6" fontWeight={600}>
          {LOADING_STAGES[loadingStage]}
        </Typography>
        <LinearProgress
          sx={{ width: '100%', maxWidth: 400, borderRadius: 2, height: 6 }}
          variant="determinate"
          value={((loadingStage + 1) / LOADING_STAGES.length) * 100}
        />
        <Typography variant="body2" color="text.secondary">
          This usually takes 10–15 seconds
        </Typography>
      </Box>
    );
  }

  // Default idle state
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AutoAwesome sx={{ color: 'primary.main' }} />
        <Typography variant="h6" fontWeight={600}>
          Import your event details
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* URL input */}
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <TextField
          fullWidth
          placeholder="https://your-event-website.com"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleUrlSubmit()}
          InputProps={{
            startAdornment: <Language sx={{ mr: 1, color: 'text.secondary' }} />,
          }}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />
        <Button
          variant="contained"
          onClick={handleUrlSubmit}
          disabled={!url.trim()}
          startIcon={<AutoAwesome />}
          sx={{ borderRadius: 2, px: 3, whiteSpace: 'nowrap' }}
        >
          Import
        </Button>
      </Box>

      {/* Divider */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ flex: 1, height: 1, bgcolor: 'grey.200' }} />
        <Typography variant="body2" color="text.secondary">or</Typography>
        <Box sx={{ flex: 1, height: 1, bgcolor: 'grey.200' }} />
      </Box>

      {/* File drop zone */}
      <Paper
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        onClick={() => fileInputRef.current?.click()}
        sx={{
          p: 4,
          border: 2,
          borderStyle: 'dashed',
          borderColor: 'grey.300',
          borderRadius: 3,
          bgcolor: '#FAFBFA',
          cursor: 'pointer',
          textAlign: 'center',
          transition: 'all 0.2s',
          '&:hover': { borderColor: 'primary.main', bgcolor: '#F5F8F6' },
        }}
      >
        <CloudUpload sx={{ fontSize: 36, color: 'text.secondary', mb: 1 }} />
        <Typography fontWeight={500}>Drop an invitation image or PDF</Typography>
        <Typography variant="body2" color="text.secondary">
          JPG, PNG, or PDF — we'll read the details with AI
        </Typography>
        <input
          ref={fileInputRef}
          type="file"
          hidden
          accept={ACCEPTED_FILE_TYPES}
          onChange={handleFileSelect}
        />
      </Paper>

      {/* Skip link */}
      <Button
        variant="text"
        onClick={onSkip}
        sx={{ alignSelf: 'center', color: 'text.secondary' }}
      >
        Skip — set up manually
      </Button>
    </Box>
  );
}


function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Remove the data:...;base64, prefix
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
